import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { cardCertificationApi } from '../../services/iso8583Api';
import { PageHeader, Card, Table, useDialect } from './common';

/**
 * The test issuer's published rules — read from the engine, so this page can
 * never describe rules the switch does not apply.
 */
export default function CardTestCards() {
  const [rules, setRules] = useState(null);
  const { mti, code } = useDialect();
  const [error, setError] = useState(null);

  useEffect(() => {
    cardCertificationApi.getSuite()
      .then((s) => setRules(s.testCardRules))
      .catch((e) => setError(e.response?.data?.message || e.message));
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader icon={CreditCard} title="Test Cards">
        When your gateway acts as acquirer, the switch answers as a scripted test issuer. The amount is the one thing every
        gateway can set without modification, so its cents choose the scenario.
      </PageHeader>

      {error && <p className="text-sm text-red-700">{error}</p>}
      {!rules && !error && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}

      {rules && (
        <>
          <Card title="Card numbers">
            <p className="text-sm text-gray-700 mb-3">Only cards from these ranges are processed:</p>
            <div className="flex flex-wrap gap-2">
              {rules.testBins.map((b) => (
                <span key={b} className="font-mono text-sm px-2.5 py-1 rounded bg-gray-100 text-gray-800">{b}…</span>
              ))}
            </div>
            <p className="text-sm text-gray-700 mt-3">
              For example <span className="font-mono">4111111111111111</span>. Anything else: <strong>{rules.outsideTestRanges}</strong>.
              Put the number in DE 2, or in track 2 (DE 35).
            </p>
          </Card>

          <Card title="The cents choose the scenario">
            <Table head={['Amount ends in', 'The switch']} mono={[0]} rows={rules.byCents.map((r) => [r.cents, r.answer])} />
            <p className="text-xs text-gray-500 mt-3">
              A withheld reply is <em>decided and recorded</em> before it is withheld — exactly what a lost reply is. It is withheld once:
              the reversal or repeat that recovers it always gets through. Each scenario is a certification case; see Certification.
            </p>
          </Card>

          <Card title="Examples (DE 4 is n12, the last two digits are the cents)">
            <Table head={['DE 4', 'Amount', 'Outcome']} mono={[0]} rows={[
              ['000000012345', '123.45', 'approved, authorisation code in DE 38'],
              ['000000010051', '100.51', `declined ${code('insufficientFunds')}`],
              ['000000010091', '100.91', `approved, reply withheld — reverse it with ${mti('0420')}`],
              ['000000010092', '100.92', `approved, reply withheld — repeat it as ${mti('0201')}`],
              ['000000010093', '100.93', `approved; reverse it, and the ${mti('0430')} is withheld — repeat as ${mti('0421')}`],
            ]} />
          </Card>
        </>
      )}
    </div>
  );
}
